/**
 * Configuration du flux d'onboarding pour le positionnement
 * 
 * Ce fichier définit toutes les étapes du flux de questions guidées
 * avec les messages, quickReplies et logique de navigation.
 * 
 * APPROCHE I18N:
 * - Les fonctions factory prennent `t` (translations) en paramètre
 * - Les textes sont tous traduits via les clés i18n
 * - Les emojis sont identiques dans toutes les langues
 */

import { OnboardingStep, OnboardingStepId, QuickReply, OnboardingData } from '../types/positioning';

// Type pour les traductions (compatible avec le système i18n existant)
export type OnboardingTranslations = {
  onboarding: {
    steps: {
      welcome: { message: string; startButton: string };
      usageType: { message: string; personal: string; company: string; custom: string };
      companyUrl: { message: string; hasUrl: string; noUrl: string; placeholder: string };
      companyName: { message: string; placeholder: string };
      personalDomain: { message: string; placeholder: string };
      purpose: { 
        message: string; 
        brandAwareness: string; 
        thoughtLeadership: string; 
        leadGeneration: string; 
        community: string; 
        recruitment: string; 
        other: string; 
      };
      tone: { 
        message: string; 
        professional: string; 
        friendly: string; 
        inspiring: string; 
        provocative: string; 
        educational: string; 
        humorous: string;
        custom: string; 
      };
      values: { message: string; placeholder: string };
      audience: { message: string; placeholder: string };
      summary: { message: string; confirm: string; restart: string };
      complete: { message: string };
    };
    summaryLabels: {
      usage: string;
      usagePersonal: string;
      usageCompany: string;
      company: string;
      domain: string;
      companyDetected: string;
      sector: string;
      purpose: string;
      tone: string;
      values: string;
      audience: string;
    };
    purposeLabels: Record<string, string>;
    toneLabels: Record<string, string>;
  };
};

// =============================================================================
// FACTORY FUNCTIONS - Génèrent les QuickReplies traduites
// =============================================================================

export function getUsageTypeReplies(t: OnboardingTranslations): QuickReply[] {
  return [
    {
      id: 'personal',
      text: t.onboarding.steps.usageType.personal,
      value: 'personal',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'company',
      text: t.onboarding.steps.usageType.company,
      value: 'company',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'custom',
      text: t.onboarding.steps.usageType.custom,
      value: 'custom',
      action: 'text_input',
      variant: 'secondary'
    }
  ];
}

export function getPurposeReplies(t: OnboardingTranslations): QuickReply[] {
  return [
    {
      id: 'brand_awareness',
      text: t.onboarding.steps.purpose.brandAwareness,
      value: 'brand_awareness',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'thought_leadership',
      text: t.onboarding.steps.purpose.thoughtLeadership,
      value: 'thought_leadership',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'lead_generation',
      text: t.onboarding.steps.purpose.leadGeneration,
      value: 'lead_generation',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'community',
      text: t.onboarding.steps.purpose.community,
      value: 'community',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'recruitment',
      text: t.onboarding.steps.purpose.recruitment,
      value: 'recruitment',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'other',
      text: t.onboarding.steps.purpose.other,
      value: 'other',
      action: 'text_input',
      variant: 'secondary'
    }
  ];
}

export function getCompanyUrlReplies(t: OnboardingTranslations): QuickReply[] {
  return [
    {
      id: 'has_url',
      text: t.onboarding.steps.companyUrl.hasUrl,
      value: 'has_url',
      action: 'url_input',
      variant: 'primary'
    },
    {
      id: 'no_url',
      text: t.onboarding.steps.companyUrl.noUrl,
      value: 'no_url',
      action: 'select',
      variant: 'secondary'
    }
  ];
}

export function getToneReplies(t: OnboardingTranslations): QuickReply[] {
  return [
    {
      id: 'professional',
      text: t.onboarding.steps.tone.professional,
      value: 'professional',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'friendly',
      text: t.onboarding.steps.tone.friendly,
      value: 'friendly',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'inspiring',
      text: t.onboarding.steps.tone.inspiring,
      value: 'inspiring',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'provocative',
      text: t.onboarding.steps.tone.provocative,
      value: 'provocative',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'educational',
      text: t.onboarding.steps.tone.educational,
      value: 'educational',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'humorous',
      text: t.onboarding.steps.tone.humorous,
      value: 'humorous',
      action: 'select',
      variant: 'outline'
    },
    {
      id: 'custom',
      text: t.onboarding.steps.tone.custom,
      value: 'custom',
      action: 'text_input',
      variant: 'secondary'
    }
  ];
}

export function getConfirmReplies(t: OnboardingTranslations): QuickReply[] {
  return [
    {
      id: 'confirm',
      text: t.onboarding.steps.summary.confirm,
      value: 'confirm',
      action: 'confirm',
      variant: 'primary'
    },
    {
      id: 'restart',
      text: t.onboarding.steps.summary.restart,
      value: 'restart',
      action: 'select',
      variant: 'secondary'
    }
  ];
}

// =============================================================================
// FACTORY - Génère toutes les étapes traduites
// =============================================================================

export function getOnboardingSteps(t: OnboardingTranslations): Record<OnboardingStepId, OnboardingStep> {
  return {
    welcome: {
      id: 'welcome',
      message: t.onboarding.steps.welcome.message,
      quickReplies: [
        {
          id: 'start',
          text: t.onboarding.steps.welcome.startButton,
          value: 'start',
          action: 'select',
          variant: 'primary'
        }
      ],
      nextStep: 'usage_type'
    },

    usage_type: {
      id: 'usage_type',
      message: t.onboarding.steps.usageType.message,
      quickReplies: getUsageTypeReplies(t),
      nextStep: {
        personal: 'personal_domain',
        company: 'company_url',
        default: 'purpose'
      },
      dataField: 'usageType'
    },

    company_url: {
      id: 'company_url',
      message: t.onboarding.steps.companyUrl.message,
      quickReplies: getCompanyUrlReplies(t),
      nextStep: {
        has_url: 'company_url',
        no_url: 'company_name',
        url_submitted: 'purpose'
      },
      inputType: 'url',
      inputPlaceholder: t.onboarding.steps.companyUrl.placeholder,
      dataField: 'companyUrl',
      skippable: true
    },

    company_name: {
      id: 'company_name',
      message: t.onboarding.steps.companyName.message,
      inputType: 'textarea',
      inputPlaceholder: t.onboarding.steps.companyName.placeholder,
      nextStep: 'purpose',
      dataField: 'companyName'
    },

    personal_domain: {
      id: 'personal_domain',
      message: t.onboarding.steps.personalDomain.message,
      inputType: 'text',
      inputPlaceholder: t.onboarding.steps.personalDomain.placeholder,
      nextStep: 'purpose',
      dataField: 'personalDomain'
    },

    purpose: {
      id: 'purpose',
      message: t.onboarding.steps.purpose.message,
      quickReplies: getPurposeReplies(t),
      nextStep: 'tone',
      dataField: 'purpose'
    },

    tone: {
      id: 'tone',
      message: t.onboarding.steps.tone.message,
      quickReplies: getToneReplies(t),
      nextStep: 'values',
      dataField: 'tone'
    },

    values: {
      id: 'values',
      message: t.onboarding.steps.values.message,
      inputType: 'textarea',
      inputPlaceholder: t.onboarding.steps.values.placeholder,
      nextStep: 'audience',
      dataField: 'values'
    },

    audience: {
      id: 'audience',
      message: t.onboarding.steps.audience.message,
      inputType: 'textarea',
      inputPlaceholder: t.onboarding.steps.audience.placeholder,
      nextStep: 'summary',
      dataField: 'audience'
    },

    summary: {
      id: 'summary',
      message: t.onboarding.steps.summary.message,
      quickReplies: getConfirmReplies(t),
      nextStep: {
        confirm: 'complete',
        restart: 'welcome'
      }
    },

    complete: {
      id: 'complete',
      message: t.onboarding.steps.complete.message,
      nextStep: 'complete'
    }
  };
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Récupère l'étape suivante en fonction de la réponse
 */
export function getNextStep(
  steps: Record<OnboardingStepId, OnboardingStep>, 
  currentStep: OnboardingStepId, 
  responseValue: string
): OnboardingStepId {
  const step = steps[currentStep];
  
  if (typeof step.nextStep === 'string') {
    return step.nextStep;
  }
  
  return step.nextStep[responseValue] || step.nextStep['default'] || 'complete';
}

/**
 * Génère le résumé des données collectées pour l'étape summary (traduit)
 */
export function generateSummary(data: OnboardingData, t: OnboardingTranslations): string {
  const lines: string[] = [];
  const labels = t.onboarding.summaryLabels;
  const purposeLabels = t.onboarding.purposeLabels;
  const toneLabels = t.onboarding.toneLabels;
  
  if (data.usageType) {
    const usageLabel = data.usageType === 'personal' ? labels.usagePersonal : labels.usageCompany;
    lines.push(`• **${labels.usage}**: ${usageLabel}`);
  }
  
  if (data.companyName) {
    lines.push(`• **${labels.company}**: ${data.companyName}`);
  }
  
  if (data.personalDomain) {
    lines.push(`• **${labels.domain}**: ${data.personalDomain}`);
  }
  
  if (data.extractedData?.companyName) {
    lines.push(`• **${labels.companyDetected}**: ${data.extractedData.companyName}`);
  }
  
  if (data.extractedData?.sector) {
    lines.push(`• **${labels.sector}**: ${data.extractedData.sector}`);
  }
  
  if (data.purpose) {
    lines.push(`• **${labels.purpose}**: ${purposeLabels[data.purpose] || data.purpose}`);
  }
  
  if (data.tone) {
    lines.push(`• **${labels.tone}**: ${toneLabels[data.tone] || data.tone}`);
  }
  
  if (data.values && data.values.length > 0) {
    const valuesStr = Array.isArray(data.values) ? data.values.join(', ') : data.values;
    lines.push(`• **${labels.values}**: ${valuesStr}`);
  }
  
  if (data.audience) {
    lines.push(`• **${labels.audience}**: ${data.audience}`);
  }
  
  return lines.join('\n');
}

/**
 * Vérifie si le flux d'onboarding doit être affiché
 */
export function shouldShowOnboarding(positioningContext: string | null, isTemplate: boolean): boolean {
  return !positioningContext || isTemplate;
}
